import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Note as NoteIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { formatDate, formatDistanceToNow, formatCurrency } from '../../utils/formatters'
import { useUserDetails } from '../../hooks/useUserDetails'

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`user-tabpanel-${index}`}
    aria-labelledby={`user-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
)

const UserDetailsDialog = ({ open, user, onClose, onUserAction }) => {
  const [activeTab, setActiveTab] = useState(0)
  const {
    data: userDetails,
    isLoading,
    error,
    refetch
  } = useUserDetails(user?.id, { enabled: open && !!user })

  useEffect(() => {
    if (open && user) {
      setActiveTab(0)
      refetch()
    }
  }, [open, user, refetch])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', color: 'success' },
      suspended: { label: 'Askıda', color: 'warning' },
      banned: { label: 'Yasaklı', color: 'error' },
      pending: { label: 'Beklemede', color: 'info' },
    }

    const config = statusConfig[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} />
  }

  const getUserAvatar = (user) => {
    const initials = user?.email?.substring(0, 2).toUpperCase() || 'UN'
    return (
      <Avatar
        sx={{
          width: 80,
          height: 80,
          fontSize: '1.5rem',
          bgcolor: user?.is_admin ? 'primary.main' : 'secondary.main'
        }}
      >
        {initials}
      </Avatar>
    )
  }

  if (!user) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Kullanıcı Detayları
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">
            Kullanıcı detayları yüklenirken hata oluştu: {error.message}
          </Alert>
        ) : (
          <>
            {/* User Header */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    {getUserAvatar(userDetails?.user || user)}
                  </Grid>

                  <Grid item xs>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {userDetails?.user?.email || user.email}
                      </Typography>
                      {getStatusChip(userDetails?.user?.status || user.status)}
                      {(userDetails?.user?.is_admin || user.is_admin) && (
                        <Chip label="Admin" color="primary" size="small" />
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ID: {userDetails?.user?.firebase_uid || user.firebase_uid || userDetails?.user?.id || user.id}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Kayıt: {formatDate(userDetails?.user?.created_at || user.created_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <CalendarIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Son Giriş: {userDetails?.user?.last_login_at ? formatDistanceToNow(userDetails.user.last_login_at) : 'Hiçbir zaman'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Düzenle">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => onUserAction('edit', userDetails?.user || user)}
                        >
                          Düzenle
                        </Button>
                      </Tooltip>

                      {(userDetails?.user?.status || user.status) === 'active' ? (
                        <Tooltip title="Askıya Al">
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<BlockIcon />}
                            onClick={() => onUserAction('suspend', userDetails?.user || user)}
                          >
                            Askıya Al
                          </Button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Aktifleştir">
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => onUserAction('activate', userDetails?.user || user)}
                          >
                            Aktifleştir
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                      {userDetails?.user?.credit_balance || user.credit_balance || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Kredi Bakiyesi
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                      {userDetails?.user?.total_rentals || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Kiralama
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                      {userDetails?.user?.total_transactions || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam İşlem
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                      {userDetails?.user?.total_spent || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Harcama
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Son Kiralamalar" />
                <Tab label="Son İşlemler" />
                <Tab label="Notlar" />
                <Tab label="Hesap Bilgileri" />
              </Tabs>
            </Box>

            {/* Recent Rentals Tab */}
            <TabPanel value={activeTab} index={0}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Son Kiralamalar
                  </Typography>
                  {userDetails?.recentRentals?.length > 0 ? (
                    <List>
                      {userDetails.recentRentals.map((rental, index) => (
                        <ListItem key={rental.id} divider={index < userDetails.recentRentals.length - 1}>
                          <ListItemIcon>
                            <PhoneIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {rental.phone_number}
                                </Typography>
                                <Chip
                                  label={rental.service_name}
                                  size="small"
                                  color="primary"
                                />
                                <Chip
                                  label={rental.status}
                                  size="small"
                                  color={rental.status === 'sms_received' ? 'success' : 'default'}
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {rental.country_name} • {rental.cost_in_credits} kredi • {formatDate(rental.created_at)}
                                </Typography>
                                {rental.sms_content && (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                    SMS: {rental.sms_content}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary">
                      Henüz kiralama yapılmamış
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            {/* Recent Transactions Tab */}
            <TabPanel value={activeTab} index={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Son İşlemler
                  </Typography>
                  {userDetails?.recentTransactions?.length > 0 ? (
                    <List>
                      {userDetails.recentTransactions.map((transaction, index) => (
                        <ListItem key={transaction.id} divider={index < userDetails.recentTransactions.length - 1}>
                          <ListItemIcon>
                            <ReceiptIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {transaction.type === 'purchase' ? 'Kredi Satın Alma' : 'Kiralama'}
                                </Typography>
                                <Chip
                                  label={transaction.status}
                                  size="small"
                                  color={transaction.status === 'completed' ? 'success' : 'default'}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {transaction.amount_in_credits > 0 ? '+' : ''}{transaction.amount_in_credits} kredi • {formatDate(transaction.created_at)}
                                {transaction.external_transaction_id && (
                                  <> • ID: {transaction.external_transaction_id}</>
                                )}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary">
                      Henüz işlem yapılmamış
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            {/* Notes Tab */}
            <TabPanel value={activeTab} index={2}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Admin Notları
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<NoteIcon />}
                      onClick={() => onUserAction('note', userDetails?.user || user)}
                    >
                      Not Ekle
                    </Button>
                  </Box>
                  <Typography color="text.secondary">
                    Admin notları özelliği yakında eklenecek
                  </Typography>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Account Info Tab */}
            <TabPanel value={activeTab} index={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Hesap Bilgileri
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <List>
                        <ListItem>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText
                            primary="Kullanıcı ID (Firebase UID)"
                            secondary={userDetails?.user?.firebase_uid || user.firebase_uid || userDetails?.user?.id || user.id}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><EmailIcon /></ListItemIcon>
                          <ListItemText
                            primary="E-posta"
                            secondary={userDetails?.user?.email || user.email}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><SecurityIcon /></ListItemIcon>
                          <ListItemText
                            primary="Dahili ID (UUID)"
                            secondary={userDetails?.user?.id || user.id}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List>
                        <ListItem>
                          <ListItemIcon><CalendarIcon /></ListItemIcon>
                          <ListItemText
                            primary="Kayıt Tarihi"
                            secondary={formatDate(userDetails?.user?.created_at || user.created_at)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CalendarIcon /></ListItemIcon>
                          <ListItemText
                            primary="Son Giriş"
                            secondary={userDetails?.user?.last_login_at ? formatDate(userDetails.user.last_login_at) : 'Hiçbir zaman'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><SecurityIcon /></ListItemIcon>
                          <ListItemText
                            primary="Kayıt IP"
                            secondary={userDetails?.user?.registration_ip || 'Bilinmiyor'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><SecurityIcon /></ListItemIcon>
                          <ListItemText
                            primary="Son Giriş IP"
                            secondary={userDetails?.user?.last_login_ip || 'Bilinmiyor'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><AttachMoneyIcon /></ListItemIcon>
                          <ListItemText
                            primary="Kredi Bakiyesi"
                            secondary={`${userDetails?.user?.credit_balance || user.credit_balance || 0} kredi`}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserDetailsDialog